import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError, Subject } from 'rxjs';
import { tap, catchError, switchMap, map } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { USER_KEY } from '@src/app/configs';
import {
  UserDto,
  UserRegisterDto,
  UserLoginDto,
  LoginResponseDto,
  MfaRequiredResponseDto,
  VerifyMfaDto,
  VerifyMfaResponseDto,
  MfaStatusDto,
  MfaEnableResponseDto,
  MfaDisableResponseDto,
  GuestLoginDto,
  GuestLoginResponseDto,
  UpgradeGuestDto,
  UpgradeGuestResponseDto,
} from '@app/modules/auth/models';
import { UserEntity, SessionEntity } from '@app/modules/auth/models';
import { PermissionsService } from '@app/shared/services/permissions.service';
import { SessionService } from './session.service';
import { MfaService } from './mfa.service';

/**
 * AuthService (Core)
 * Odpowiedzialny za:
 * - Login, logout, register
 * - Guest login i upgrade
 * - User management
 * - Integration z SessionService i MfaService
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Obserwowalne stany
  public currentUser$ = new BehaviorSubject<UserEntity | null>(null);
  public isGuest$ = new BehaviorSubject<boolean>(false);
  public clientType$ = new BehaviorSubject<'web' | 'mobile'>('web');

  // Computed observable - użytkownik jest zalogowany gdy ma dane w currentUser$
  public isLoggedIn$ = this.currentUser$.pipe(map(user => !!user));

  // @deprecated - użyj isLoggedIn$ zamiast tego
  public isAuthenticated$ = this.isLoggedIn$;

  // Subject dla zmian w użytkowniku
  private userChangedSubject = new Subject<UserEntity | null>();
  public userChanged$ = this.userChangedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private permissionsService: PermissionsService,
    private sessionService: SessionService,
    private mfaService: MfaService
  ) {
    this.initializeAuthentication();
    this.detectClientType();
  }

  /**
   * Zwykłe logowanie z email/hasłem
   */
  login(credentials: UserLoginDto): Observable<LoginResponseDto | MfaRequiredResponseDto> {
    return this.http
      .post<LoginResponseDto | MfaRequiredResponseDto>(
        `${environment.apiUrl}auth/login`,
        credentials,
        this.getHttpOptions()
      )
      .pipe(
        tap((response: any) => {
          // Jeśli wymagane MFA
          if (response.requiresMfa || response.tempToken) {
            this.mfaService.setMfaSession(response as MfaRequiredResponseDto);
          } else {
            // Sukces logowania
            const loginResponse = response as LoginResponseDto;
            this.handleLoginSuccess(loginResponse);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          // 403 oznacza MFA required
          if (this.mfaService.handleMfaError(error)) {
            return throwError(() => error.error);
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Weryfikacja MFA kodu
   */
  verifyMfa(dto: VerifyMfaDto): Observable<VerifyMfaResponseDto> {
    return this.mfaService.verifyMfa(dto).pipe(
      tap((response: VerifyMfaResponseDto) => {
        this.handleLoginSuccess(response);
      })
    );
  }

  /**
   * Guest logowanie
   */
  guestLogin(dto: GuestLoginDto): Observable<GuestLoginResponseDto> {
    return this.http
      .post<GuestLoginResponseDto>(
        `${environment.apiUrl}auth/guest-login`,
        dto
      )
      .pipe(
        tap((response: GuestLoginResponseDto) => {
          this.handleLoginSuccess(response);
          this.isGuest$.next(true);
        }),
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Upgrade gościa na zwykłego użytkownika
   */
  upgradeGuest(dto: UpgradeGuestDto): Observable<UpgradeGuestResponseDto> {
    return this.http
      .post<UpgradeGuestResponseDto>(
        `${environment.apiUrl}users/upgrade-guest`,
        dto
      )
      .pipe(
        tap((response: UpgradeGuestResponseDto) => {
          const userDto: UserDto = {
            id: response.id,
            firstName: response.firstName,
            lastName: response.lastName,
            photo: response.photo,
            role: response.role,
            email: response.email,
            permissions: response.permissions || []
          };
          const user = new UserEntity(userDto);
          this.currentUser$.next(user);
          this.userChangedSubject.next(user);
          this.setUser(userDto);
          this.isGuest$.next(false);
        }),
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Rejestracja nowego użytkownika
   */
  register(dto: UserRegisterDto): Observable<any> {
    return this.http
      .post<any>(
        `${environment.apiUrl}auth/register`,
        dto,
        this.getHttpOptions()
      )
      .pipe(
        catchError((error) => throwError(() => error))
      );
  }

  /**
   * Refresh tokenu (session rotation)
   */
  refreshToken(): Observable<any> {
    return this.sessionService.refreshToken().pipe(
      tap((response: LoginResponseDto) => {
        // Zaktualizuj użytkownika jeśli są dane w response
        if (response.id) {
          const userDto: UserDto = {
            id: response.id,
            firstName: response.firstName,
            lastName: response.lastName,
            photo: response.photo,
            role: response.role,
            email: response.email,
            permissions: response.permissions || []
          };
          const user = new UserEntity(userDto);
          this.currentUser$.next(user);
          this.setUser(userDto);
          this.userChangedSubject.next(user);
        }
      }),
      catchError((error) => {
        this.logout();
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout
   */
  logout(): void {
    this.sessionService.clearSession();
    this.mfaService.clearMfaSession();
    localStorage.removeItem(USER_KEY);
    this.currentUser$.next(null);
    this.isGuest$.next(false);
    this.permissionsService.clearPermissions();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Wymuszone wylogowanie (wywołane przez SessionService gdy token wygasł)
   * Nie nawiguje - interceptor już to robi
   */
  private handleForcedLogout(): void {
    console.log('[AuthService] Handling forced logout');
    this.mfaService.clearMfaSession();
    this.currentUser$.next(null);
    this.isGuest$.next(false);
    this.permissionsService.clearPermissions();
  }

  // ===== MFA MANAGEMENT METHODS =====

  /**
   * Pobierz status MFA użytkownika
   */
  getMfaStatus(): Observable<MfaStatusDto> {
    return this.mfaService.getMfaStatus().pipe(
      tap(() => {
        // Odśwież dane użytkownika
        this.refreshUserData();
      })
    );
  }

  /**
   * Włącz MFA dla użytkownika
   */
  enableMfa(): Observable<MfaEnableResponseDto> {
    return this.mfaService.enableMfa().pipe(
      tap(() => {
        this.updateUserMfaStatus(true);
      })
    );
  }

  /**
   * Wyłącz MFA dla użytkownika
   */
  disableMfa(): Observable<MfaDisableResponseDto> {
    return this.mfaService.disableMfa().pipe(
      tap(() => {
        this.updateUserMfaStatus(false);
      })
    );
  }

  /**
   * Czy wymagane jest MFA
   */
  isMfaRequired(): boolean {
    return this.mfaService.isMfaRequired();
  }

  /**
   * Observable dla statusu MFA
   */
  get mfaRequired$(): Observable<boolean> {
    return this.mfaService.mfaRequired$;
  }

  /**
   * Żądanie resetu hasła
   */
  requestPasswordReset(email: string, clientId?: string | null): Observable<any> {
    const payload: any = { email };
    if (clientId) {
      payload.client_id = clientId;
    }
    return this.http
      .post<any>(`${environment.apiUrl}auth/request-password-reset`, payload)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Aktywacja konta
   * Wymaga token i email z linku aktywacyjnego
   */
  activateAccount(token: string, email: string): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}auth/activate-account`, { token, email })
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Ponowne wysłanie linku aktywacyjnego
   * Używane gdy poprzedni link wygasł (15 minut)
   */
  resendActivationLink(email: string): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}auth/resend-activation`, { email })
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Ustawienie nowego hasła (reset password)
   */
  passwordSetup(payload: { token: string; password: string; confirmPassword: string; client_id?: string }): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}auth/reset-password`, payload)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Alias dla getCurrentUser - kompatybilność wsteczna
   */
  getUser(): UserEntity | null {
    return this.getCurrentUser();
  }

  /**
   * Czy token wygasa wkrótce (w ciągu 1 minuty)
   */
  isTokenExpiringSoon(): boolean {
    return this.sessionService.isTokenExpiringSoon();
  }

  /**
   * Czy użytkownik jest zalogowany
   */
  isAuthenticated(): boolean {
    return this.sessionService.isTokenValid();
  }

  /**
   * Czy użytkownik jest gościem
   */
  isGuest(): boolean {
    return this.isGuest$.value;
  }

  /**
   * Pobierz obecnego użytkownika
   */
  getCurrentUser(): UserEntity | null {
    return this.currentUser$.value;
  }

  /**
   * Pobierz access token
   */
  getAccessToken(): string | null {
    return this.sessionService.getAccessToken();
  }

  /**
   * Pobierz refresh token
   */
  getRefreshToken(): string | null {
    return this.sessionService.getRefreshToken();
  }

  /**
   * Ustaw access token w nagłówku Authorization
   */
  getAuthorizationHeader(): string | null {
    return this.sessionService.getAuthorizationHeader();
  }

  // ===== PRIVATE METHODS =====

  /**
   * Obsługa sukcesu logowania
   */
  private handleLoginSuccess(response: LoginResponseDto | VerifyMfaResponseDto): void {
    const token = response.access_token;
    const refreshToken = response.refresh_token;
    
    // Utwórz UserDto z odpowiedzi
    const userDto: UserDto = {
      id: response.id,
      firstName: response.firstName,
      lastName: response.lastName,
      photo: response.photo,
      role: response.role,
      email: response.email,
      permissions: response.permissions || []
    };
    
    const user = new UserEntity(userDto);

    // Wyciągnij exp z JWT
    const expiresIn = this.sessionService.getTokenExpiresInSeconds(token);

    // Ustaw sesję
    this.sessionService.setSession(new SessionEntity(token, expiresIn, refreshToken));

    // Ustaw użytkownika
    this.currentUser$.next(user);
    this.setUser(userDto);
    this.userChangedSubject.next(user);

    // Ustaw uprawnienia
    if (user.permissions) {
      this.permissionsService.setUserPermissions(user.permissions as any);
    }
  }

  /**
   * Inicjalizuj autentykację przy starcie aplikacji
   * Dla WEB: HttpOnly Cookie może być ważne nawet gdy access token wygasł
   */
  private initializeAuthentication(): void {
    // Nasłuchuj na wylogowanie z SessionService (gdy interceptor wykryje wygaśnięcie tokena)
    this.sessionService.logoutRequired$.subscribe(() => {
      console.log('[AuthService] Received logout signal from SessionService');
      this.handleForcedLogout();
    });

    const userJson = localStorage.getItem(USER_KEY);
    const isMobile = this.isMobilePlatform();

    if (this.sessionService.isTokenValid() && userJson) {
      try {
        const user = JSON.parse(userJson);
        const userEntity = new UserEntity(user);

        this.currentUser$.next(userEntity);
        if (userEntity.isGuest) {
          this.isGuest$.next(true);
        }

        // Ustaw uprawnienia jeśli są
        if (userEntity.permissions) {
          this.permissionsService.setUserPermissions(userEntity.permissions as any);
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        this.logout();
      }
    } else if (userJson) {
      // Access token wygasł ale mamy dane użytkownika
      // DUAL MODE:
      // Web: HttpOnly Cookie może być nadal ważne - zachowaj użytkownika, interceptor odświeży token przy pierwszym API call
      // Mobile: refresh token w localStorage - jeśli go nie ma, wyloguj
      
      if (!isMobile) {
        // WEB: Zachowaj użytkownika - HttpOnly Cookie może być ważne
        console.log('[AuthService] Access token expired (web) - keeping user, will refresh on first API call');
        try {
          const user = JSON.parse(userJson);
          const userEntity = new UserEntity(user);
          this.currentUser$.next(userEntity);
          if (userEntity.isGuest) {
            this.isGuest$.next(true);
          }
          if (userEntity.permissions) {
            this.permissionsService.setUserPermissions(userEntity.permissions as any);
          }
        } catch (error) {
          console.error('Failed to restore user:', error);
          this.logout();
        }
      } else {
        // MOBILE: Sprawdź czy mamy refresh token
        const hasRefreshToken = !!this.sessionService.getRefreshToken();
        if (hasRefreshToken) {
          console.log('[AuthService] Access token expired (mobile) - keeping user, will refresh on first API call');
          try {
            const user = JSON.parse(userJson);
            const userEntity = new UserEntity(user);
            this.currentUser$.next(userEntity);
            if (userEntity.isGuest) {
              this.isGuest$.next(true);
            }
            if (userEntity.permissions) {
              this.permissionsService.setUserPermissions(userEntity.permissions as any);
            }
          } catch (error) {
            console.error('Failed to restore user:', error);
            this.logout();
          }
        } else {
          console.log('[AuthService] Token invalid and no refresh token (mobile) - logging out');
          this.logout();
        }
      }
    }
  }

  /**
   * Sprawdź czy to platforma mobile
   */
  private isMobilePlatform(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /mobile|android|ios|iphone|ipad|ipod/.test(userAgent);
  }

  /**
   * Zaktualizuj status MFA użytkownika
   */
  private updateUserMfaStatus(enabled: boolean): void {
    const currentUser = this.currentUser$.value;
    if (currentUser) {
      currentUser.mfaEnabled = enabled;
      this.currentUser$.next(currentUser);
      this.userChangedSubject.next(currentUser);
      // Zaktualizuj w localStorage
      const userDto: UserDto = {
        id: currentUser.id,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        photo: currentUser.photo,
        role: currentUser.role,
        email: currentUser.email,
        permissions: currentUser.permissions
      };
      this.setUser(userDto);
    }
  }

  /**
   * Odśwież dane użytkownika z serwera
   */
  private refreshUserData(): void {
    // Możesz dodać endpoint do pobierania aktualnych danych użytkownika
    // Na razie aktualizujemy tylko lokalny stan
    const currentUser = this.currentUser$.value;
    if (currentUser) {
      this.userChangedSubject.next(currentUser);
    }
  }

  /**
   * Ustaw użytkownika w local storage
   */
  private setUser(user: UserDto): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  /**
   * Detectuj typ klienta (web/mobile)
   */
  private detectClientType(): void {
    const userAgent = navigator.userAgent;
    const isMobile = /mobile|tablet|android|ios/i.test(userAgent);
    const clientType = isMobile ? 'mobile' : 'web';
    this.clientType$.next(clientType as 'web' | 'mobile');
  }

  /**
   * Opcje HTTP z X-Client-Type headerem i withCredentials (dla dual-mode auth)
   * withCredentials: true - pozwala przeglądarce wysyłać/odbierać HttpOnly Cookies
   */
  private getHttpOptions(): any {
    return {
      headers: {
        'X-Client-Type': this.clientType$.value,
      },
      withCredentials: true,  // WAŻNE: Bez tego przeglądarka nie zaakceptuje HttpOnly Cookie!
    };
  }
}
